import { reduce } from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallCleanAdminPool } from "../../../api/auditor-api/api-call-clean-admin-pool";
import { apiCallGetAllAdminsPool } from "../../../api/auditor-api/api-call-get-all-admin-pools";
import { TAdminPools, TPoolTable } from "../../../Types";
import getApproveRejectStore from "../../approval-reject-store";
import getCorePersonalStore from "../../core/core-personal-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import getInvestigationCasesStore from "../../investigation-cases-store";
import getReportsStore from "../../reports-store";
import getAuditStore from "../audit-store";

class PoolStore {
  adminApprovalPoolData = observable.box<TAdminPools>([]);
  adminReportPoolData = observable.box<TAdminPools>([]);
  adminInvestigationCasesPoolData = observable.box<TAdminPools>([]);
  adminAuditEventsPool = observable.box<TAdminPools>([]);

  adminApprovalPoolDataOrdered = computed(() => {
    const adminApprovalPoolData: TAdminPools = orderBy(
      this.adminApprovalPoolData.get(),
      ["adminId"],
      ["asc"]
    );
    return adminApprovalPoolData;
  });
  adminReportPoolDataOrdered = computed(() => {
    const adminReportPoolData: TAdminPools = orderBy(
      this.adminReportPoolData.get(),
      ["adminId"],
      ["asc"]
    );
    return adminReportPoolData;
  });
  adminInvestigationCasesPoolDataOrdered = computed(() => {
    const adminReportPoolData: TAdminPools = orderBy(
      this.adminInvestigationCasesPoolData.get(),
      ["adminId"],
      ["asc"]
    );
    return adminReportPoolData;
  });
  adminAuditEventsPoolOrdered = computed(() => {
    const adminReportPoolData: TAdminPools = orderBy(
      this.adminAuditEventsPool.get(),
      ["adminId"],
      ["asc"]
    );
    return adminReportPoolData;
  });
  getPool = async () => {
    getCoreUIStore().setIsLoading("AUDITOR_POOL", true);
    const { data, status, code } = await apiCallGetAllAdminsPool();
    const adminApprovalPool = reduce(() => data.adminApprovalPool, []);
    const adminReportPool = reduce(() => data.adminReportPool, []);
    const adminInvestigationCasesPool = reduce(
      () => data.adminInvestigationCasesPool,
      []
    );
    const adminAuditEventsPool = reduce(() => data.adminAuditEventsPool, []);
    if (status === "OK") {
      runInAction(() => {
        this.adminApprovalPoolData.set(adminApprovalPool);
        this.adminReportPoolData.set(adminReportPool);
        this.adminInvestigationCasesPoolData.set(adminInvestigationCasesPool);
        this.adminAuditEventsPool.set(adminAuditEventsPool);
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("AUDITOR_POOL", false);
  };
  cleanPool = async (adminID: number, poolType: TPoolTable) => {
    getCoreUIStore().setIsLoading("AUDITOR_POOL", true);
    const { code, status } = await apiCallCleanAdminPool(adminID, poolType);
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage("POOL_CLEANED");
      const myID = getCorePersonalStore().user.get().id;
      if (adminID === myID) {
        poolType === "APPROVAL"
          ? getApproveRejectStore().setApprovalData([])
          : poolType === "REPORT"
            ? getReportsStore().setPendingReports({})
            : poolType === "INVESTIGATION_CASES"
              ? getInvestigationCasesStore().setInvestigationCases([])
              : getAuditStore().resetAll();
      }
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("AUDITOR_POOL", false);
    await this.getPool();
  };

  getPoolData = () => {
    const adminApprovalPoolDataOrdered =
      getPoolStore().adminApprovalPoolDataOrdered.get();
    const adminApprovalPoolData = adminApprovalPoolDataOrdered.map((data) => {
      return {
        adminId: data.adminId,
        fullName: data.fullName,
        totalCount: data.totalCount,
        cleanPool: {
          totalCount: data.totalCount,
          cleanPoolAdminId: data.adminId,
          cleanPoolType: "APPROVAL"
        }
      };
    });
    const adminReportPoolDataOrdered =
      getPoolStore().adminReportPoolDataOrdered.get();
    const adminReportPoolData = adminReportPoolDataOrdered.map((data) => {
      return {
        adminId: data.adminId,
        fullName: data.fullName,
        totalCount: data.totalCount,
        cleanPool: {
          totalCount: data.totalCount,
          cleanPoolAdminId: data.adminId,
          cleanPoolType: "REPORT"
        }
      };
    });
    const adminInvestigationCasesPoolDataOrdered =
      getPoolStore().adminInvestigationCasesPoolDataOrdered.get();
    const adminInvestigationCasesPoolData =
      adminInvestigationCasesPoolDataOrdered.map((data) => {
        return {
          adminId: data.adminId,
          fullName: data.fullName,
          totalCount: data.totalCount,
          cleanPool: {
            totalCount: data.totalCount,
            cleanPoolAdminId: data.adminId,
            cleanPoolType: "INVESTIGATION_CASES"
          }
        };
      });
    const adminAuditEventsPoolOrdered =
      getPoolStore().adminAuditEventsPoolOrdered.get();
    const adminAuditEventsPool = adminAuditEventsPoolOrdered.map((data) => {
      return {
        adminId: data.adminId,
        fullName: data.fullName,
        totalCount: data.totalCount,
        cleanPool: {
          totalCount: data.totalCount,
          cleanPoolAdminId: data.adminId,
          cleanPoolType: "AUDITOR_AUDIT_EVENTS"
        }
      };
    });
    return {
      adminApprovalPoolData,
      adminReportPoolData,
      adminInvestigationCasesPoolData,
      adminAuditEventsPool
    };
  };
}
export const getPoolStore = memoize(
  () => new PoolStore(),
  () => 1
);
export default getPoolStore;
